import { CheckoutDeliveryOption, DeliveryOption } from '@/gql';
import { StripeAddressElementOptions, StripePaymentElementOptions } from '@stripe/stripe-js';

export const CART_REDIRECT_PART = '/cart';
export const ADDRESS_VERIFICATION_SELECTION = {
  SUGGESTED: 'suggested',
  ORIGINAL: 'original',
};

/**
 * Checkout related constants
 */
export const SPRING_DEFAULT_BUSINESS_NAME = 'Spring by Amaze';
export const CheckoutSteps = {
  INIT: 0,
  AWAITING_CUSTOMER_INFO: 1,
  AWAITING_SHIPPING_OPTION: 2,
  AWAITING_PAYMENT_INFO: 3,
  CONFIRMATION: 4,
  COMPLETE: 5,
} as const;

export const CARD_CHECKOUT_ADDRESS_OPTIONS: StripeAddressElementOptions = {
  mode: 'shipping',
  blockPoBox: true,
  fields: {
    phone: 'always',
  },
  validation: {
    phone: {
      required: 'always',
    },
  },
  autocomplete: {
    mode: 'automatic',
  },
  defaultValues: {},
};

export const CARD_CHECKOUT_PAYMENT_OPTIONS: StripePaymentElementOptions = {
  layout: {
    type: 'accordion',
    defaultCollapsed: false,
    radios: true,
    spacedAccordionItems: false,
  },
};

export const DELIVERY_OPTIONS = ['fullRushCost', 'fullStandardCost', 'fullSuperRushCost'] as const;
export const DELIVERY_OPTION_TO_CHECKOUT_DELIVERY_OPTION: Record<DeliveryOption, CheckoutDeliveryOption> = {
  standard: 'fullStandardCost',
  rush: 'fullRushCost',
  super_rush: 'fullSuperRushCost',
};
export const CHECKOUT_DELIVERY_OPTION_TO_DELIVERY_OPTION: Record<CheckoutDeliveryOption, DeliveryOption> = {
  fullStandardCost: 'standard',
  fullRushCost: 'rush',
  fullSuperRushCost: 'super_rush',
};

export const ERROR_CONTEXTS = ['checkout', 'customer', 'confirm', 'express', 'confirm_redirect'] as const;
export const CHECKOUT_ERROR_CTX = ERROR_CONTEXTS[0];
export const CUSTOMER_ERROR_CTX = ERROR_CONTEXTS[1];
export const CONFIRM_ERROR_CTX = ERROR_CONTEXTS[2];
export const EXPRESS_ERROR_CTX = ERROR_CONTEXTS[3];
export const CONFIRM_REDIRECT_ERROR_CTX = ERROR_CONTEXTS[4];

type ErrorContext = (typeof ERROR_CONTEXTS)[number];
const createCheckoutError = (displayMessage: string, ctx: ErrorContext) => ({ displayMessage, ctx });

// Attempt to actually collect the errors that can occur during checkout
export const DEFAULT_ERROR_MESSAGE = 'An unknown error occurred. Please try again later.';

export const CHECKOUT_ERRORS = {
  INITIALIZE_TIMEOUT_ERROR: createCheckoutError(
    'Error initializing checkout due to missing required data. Please try again.',
    CHECKOUT_ERROR_CTX
  ),
  INITIALIZATION_ERROR: createCheckoutError('Error initializing checkout. Please try again.', 'checkout'),
  SHIPPING_ESTIMATE_ERROR: createCheckoutError('', 'checkout'), // To be handled like other errors - but not really an error
  INVALID_STORE_SLUG: createCheckoutError('Invalid store slug. Please reload the page and try again.', 'checkout'),
  INVALID_LINE_ITEMS: createCheckoutError(
    'Cart line items are invalid. Please check your cart and try again.',
    CHECKOUT_ERROR_CTX
  ),
  EMPTY_CART: createCheckoutError('Cart is empty. Please add items to your cart and try again.', CHECKOUT_ERROR_CTX),
  INVALID_CHECKOUT_STEP: createCheckoutError('Unexpected error occurred. Please try again.', CHECKOUT_ERROR_CTX),
  INVALID_DELIVERY_OPTIONS: createCheckoutError(
    'Error getting delivery options based on location. Please reload the page and try again.',
    CHECKOUT_ERROR_CTX
  ),
  SUBMIT_ELEMENTS_UNEXPECTED_ERROR: createCheckoutError(
    'Unexpected error occurred during checkout validation. Please reload the page and try again.',
    CHECKOUT_ERROR_CTX
  ),
  SMARTY_STREETS_INVALID_ERROR: createCheckoutError(
    'Address is not a valid location to ship to. Please check your information and try again.',
    CUSTOMER_ERROR_CTX
  ),
  MISSING_ADDRESS_DATA: createCheckoutError(
    'Address is missing required data. Please check your information and try again.',
    CUSTOMER_ERROR_CTX
  ),
  INVALID_ADDRESS: createCheckoutError(
    'Address is not valid for shipping. Please check and edit your information and try again.',
    CUSTOMER_ERROR_CTX
  ),
  INVALID_CHECKOUT: createCheckoutError(
    'Invalid checkout data. Please check your information and try again.',
    CHECKOUT_ERROR_CTX
  ),
  MINERVA_ERROR: createCheckoutError(
    'Invalid shipping region or location. Please try reloading the page.',
    CHECKOUT_ERROR_CTX
  ),
  UNPROCESSABLE_SHIPPING_RATES: createCheckoutError(
    'Unable to get shipping rates for selected address. Please verify and try again.',
    CUSTOMER_ERROR_CTX
  ),
  SUBMIT_PAYMENT_ELEMENTS_ERROR: createCheckoutError(
    'Unable to checkout using the provided information. Please verify the information is correct and try again.',
    CONFIRM_ERROR_CTX
  ),
  CONFIRMPAGE_PAYMENTINTENT_FAILURE: createCheckoutError(
    'Unable to complete payment confirmation. Please try again.',
    CONFIRM_REDIRECT_ERROR_CTX
  ),
  CONFIRMPAGE_NOT_SUCCEEDED: createCheckoutError(
    'Payment was not successful. Please try again.',
    CONFIRM_REDIRECT_ERROR_CTX
  ),
  CONFIRMPAGE_STRIPE_FAILURE: createCheckoutError(
    'Unexpected error occurred processing payment. Please try again.',
    CONFIRM_REDIRECT_ERROR_CTX
  ),
  CONFIRMPAGE_MISSING_PARAMS: createCheckoutError(
    'Missing required parameters to confirm payment. Please try reloading the page and try again.',
    CONFIRM_REDIRECT_ERROR_CTX
  ),
  CONFIRMPAGE_PAYMENTINTENT_NOT_SUCCEEDED: createCheckoutError(
    'There was an error processing your payment. Please verify your information and try again.',
    CONFIRM_REDIRECT_ERROR_CTX
  ),
  EXPRESS_SHIPPING_ERROR: createCheckoutError(
    'Unable to use shipping information provided by the express checkout.',
    EXPRESS_ERROR_CTX
  ),
  EXPRESS_MISSING_BILLING_INFO_ERROR: createCheckoutError(
    'Missing required information from payment provider. Please try checking out again.',
    EXPRESS_ERROR_CTX
  ),
  EXPRESS_SHIPPING_RATE_ERROR: createCheckoutError(
    'Unable to update rates for new shipping option. Please try again.',
    EXPRESS_ERROR_CTX
  ),
  EXPRESS_INITIATE_PAYMENT_ERROR: createCheckoutError(
    'Unable to confirm payment information. Please check the values and try again.',
    EXPRESS_ERROR_CTX
  ),
  EXPRESS_PAYMENT_PROCESS_ERROR: createCheckoutError(
    'Unable to process payment. Please verify your information and try again.',
    EXPRESS_ERROR_CTX
  ),
  EXPRESS_ELEMENT_ERROR: createCheckoutError('', EXPRESS_ERROR_CTX), // message comes from Stripe
  EXPRESS_CONFIRMATION_ERROR: createCheckoutError(
    'Missing required information for checkout. Please verify all fields are completed and try again.',
    EXPRESS_ERROR_CTX
  ),
  UNKNOWN_ERROR__CHECKOUT: createCheckoutError(
    'Unexpected error occurred. Please reload the page to try again.',
    CHECKOUT_ERROR_CTX
  ),
  UNKNOWN_ERROR__CUSTOMER: createCheckoutError(
    'Unexpected error occurred updating address or shipping information. Please try again.',
    CUSTOMER_ERROR_CTX
  ),
  UNKNOWN_ERROR__CONFIRM: createCheckoutError(
    'Unexpected error occurred during submission of checkout. Please try again.',
    CONFIRM_ERROR_CTX
  ),
  UNKNOWN_ERROR__EXPRESS: createCheckoutError(
    'Unexpected error occurred during express checkout. Please try again.',
    EXPRESS_ERROR_CTX
  ),
  GENERIC_ERROR: createCheckoutError(
    'Unexpected error occurred processing checkout request. Please try again.',
    CHECKOUT_ERROR_CTX
  ),
  CONFIRMATION_TOKEN_GENERATION_FAILURE: createCheckoutError(
    'Missing or payment information. Please verify your card information and try again.',
    CONFIRM_ERROR_CTX
  ),
  SUBMIT_PAYMENT_ERROR: createCheckoutError(
    'Error submitting payment information. Please verify and try again.',
    CONFIRM_ERROR_CTX
  ),
  PAYMENT_INTENT_CREATE_ERROR: createCheckoutError(
    'Error attempting to make payment. Please verify and try again.',
    CONFIRM_ERROR_CTX
  ),
  CHECKOUT_ELEMENTS_SUBMIT_FAILURE: createCheckoutError(
    'Error submitting due to missing payment information. Please verify all required data is filled out and try again.',
    CONFIRM_ERROR_CTX
  ),
  CHECKOUT_SERVICE_SUBMISSION_FAILURE: createCheckoutError(
    'Error submitting checkout information. Please verify your data and try again, or reload the page and try again.',
    CONFIRM_ERROR_CTX
  ),
  STRIPE_CARD_ERROR: createCheckoutError(
    'Error processing payment. Please verify your information and try again.',
    CONFIRM_ERROR_CTX
  ),
  PAYPAL_PAYMENT_FAILURE: createCheckoutError(
    'Error processing payment. Please verify your information and try again.',
    CONFIRM_ERROR_CTX
  ),
};
